import { Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { api, getQueryProps } from 'common';

import { FeatureFlagKey, useFlag } from '../../../../../../../utils/flags';
import { useFlowDefinitionContext } from '../../../../context';
import { BlockAutocomplete } from '../../ConfigFields/Autocomplete';
import { BlockTagsFields } from '../../ConfigFields/BlockTagsFields';
import { BlockTextArrayField } from '../../ConfigFields/CodeMirror/BlockTextArray';
import { BlockValueTemplateField } from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

const { ConversationTagEditMode } = api.FlowDefinition;

const TAG_EDIT_DISPLAY_VALUES: Record<string, string> = {
  [ConversationTagEditMode.Edit]: 'Edit (Add/Remove)',
  [ConversationTagEditMode.Replace]: 'Replace All',
};

export function UpdateModeFormFields() {
  const { flowDefinition } = useFlowDefinitionContext();
  const { isEnabledOnBlock: isAssignmentGroupEnabled } = useFlag({
    flagKey: FeatureFlagKey.conversationTaskAssignmentGroup,
  });

  const { data: assignmentGroups = [] } = useQuery({
    ...getQueryProps(api.client.AssignmentGroup, 'findAll', {
      organizationId: flowDefinition.organizationId,
      limit: 500,
    }),
    enabled: isAssignmentGroupEnabled,
  });

  const { value: tagEditMode } =
    useSelectedNodeField<api.FlowDefinition.ConversationTagEditMode>('config.tagEditMode');

  return (
    <Stack gap={2}>
      <BlockValueTemplateField
        fieldName="config.existingConversationId"
        label="Existing Conversation ID"
        helperText="Provide the existing conversation ID to update"
        required
      />
      <BlockAutocomplete
        fieldName="config.tagEditMode"
        label="Tag Edit Mode"
        options={Object.values(ConversationTagEditMode)}
        getOptionLabel={(tagEditMode) => TAG_EDIT_DISPLAY_VALUES[tagEditMode]}
        helperText="Select a tag edit mode to use for this block"
      />
      {isAssignmentGroupEnabled && (
        <BlockSelectField
          label="Assignment Group"
          fieldName="config.assignmentGroupId"
          fieldVariant="SelectOrBlockValueTemplate"
          options={assignmentGroups.map((assignmentGroup) => ({
            label: assignmentGroup.name,
            value: assignmentGroup.id,
          }))}
        />
      )}

      {tagEditMode === ConversationTagEditMode.Edit && (
        <>
          <BlockTextArrayField
            label="Tags to Add"
            addRowLabel="Add"
            fieldName="config.tagsToAdd"
            placeholder="Enter tag to add"
          />
          <BlockTextArrayField
            label="Tags to Remove"
            addRowLabel="Add"
            fieldName="config.tagsToRemove"
            placeholder="Enter tag to remove"
          />
        </>
      )}

      {tagEditMode === ConversationTagEditMode.Replace && <BlockTagsFields />}
    </Stack>
  );
}
